import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ActionableNotification, Button, InlineLoading } from '@carbon/react';
import { View, ViewOff } from '@carbon/react/icons';
import { useAtomValue, useSetAtom } from 'jotai';
import { useStore } from '@tanstack/react-store';
import classnames from 'classnames';

import { useWizardStep } from 'components/full-page-wizard';
import { TypeCurveScenarioPicker } from 'components/type-curve-scenario-picker/type-curve-scenario-picker';

import { AddLtdModal } from '../add-ltd-modal';
import { AddWellboreModal } from '../add-wellbore-modal';
import {
  currentDraftSummaryAtom,
  draftAssetChangePendingAtom,
  draftMapFeaturesAtom,
  draftSelectedAssetCountAtom,
  subtractTicksPendingAtom,
} from '../atoms';
import { contributingFileName } from '../asset-list-utils';
import { usePackageWizardCtx } from '../context';
import { useAssetListSummary } from '../hooks';
import { PackageWizardMap } from '../package-wizard-map';
import { entityOptionsFromSources } from '../utils';
import { AssetsSlotsSplitPane } from './assets-slots-split-pane';
import { AssetsTable } from './assets-table';
import { SlotsTable } from './slots-table';
import { Step1SummaryPanel } from './step1-summary-panel';
import styles from './select-assets-slots-step.module.scss';

/**
 * Step 1 — Select Assets & Slots. Three-column layout (tables / map /
 * summary) with per-column show/hide toggles. Both tables are BE-backed:
 * `AssetsTable` drives asset membership via
 * `PATCH /packages/drafts/:id/assets`; `SlotsTable` drives slot selection
 * via `PATCH /packages/drafts/:id/slot_selections`. Neither selection set
 * lives on the form — the draft is the server-side source of truth and
 * `POST /packages` (Step 2) reads from it directly.
 *
 * Selection state is owned by `AssetsTable` (and by the draft-slots
 * response for `SlotsTable`); this step no longer tracks it. The
 * `AssetsTable` remounts via `key={draftId}` whenever a new draft is
 * created so its local selection resets cleanly.
 *
 * Layout: CSS Grid with 4 permutations driven by `showMap` + `showSummary`.
 * Tables column absorbs the remainder; map and summary are fixed-width per
 * the v5 mock (`400px` / `364px`), with the map growing to `764px` when
 * summary is hidden to preserve the visual balance.
 *
 * Next gates on at least one selected asset, derived from
 * `draftSelectedAssetCountAtom`. That atom rolls up the same summary
 * `AssetsTable` / `SlotsTable` write on every successful query and
 * PATCH, so the gate stays in sync with the BE without a separate
 * count fetch. Pre-first-fetch the count is 0 → Next stays disabled.
 *
 * @return {JSX.Element} Step 1 content
 */
export const SelectAssetsSlotsStep: FC = () => {
  const { form, api } = usePackageWizardCtx();
  const { setNextEnabled, setBackEnabled } = useWizardStep();

  const sourceType = useStore(form.store, (s) => s.values.sourceType);
  const sources = useStore(form.store, (s) => s.values.sources);
  const draftId = useStore(form.store, (s) => s.values.draftId);
  const scenarioId = useStore(form.store, (s) => s.values.typeCurveScenarioId);
  const assetListFile = useStore(form.store, (s) => s.values.assetList.file);
  const assetList = useAssetListSummary(form);
  const assetListFileName = useMemo(
    () => contributingFileName(assetListFile, assetList.matched),
    [assetListFile, assetList.matched]
  );
  const hasSelectedAsset = useAtomValue(draftSelectedAssetCountAtom) > 0;
  // Subtract ticks waiting out their pause hold Back and the scenario switch,
  // so neither leaves or rebuilds the draft before the ticks are sent.
  const ticksPending = useAtomValue(subtractTicksPendingAtom);
  // Next waits longer: until an asset change is sent and its build is done, since
  // Summary reads the draft and Create refuses one still building.
  const assetChangePending = useAtomValue(draftAssetChangePendingAtom);
  const fetchMapFeatures = useSetAtom(draftMapFeaturesAtom);
  const resetDraftSummary = useSetAtom(currentDraftSummaryAtom);

  const [showMap, setShowMap] = useState(true);
  const [showSummary, setShowSummary] = useState(true);
  const [addWellboreOpen, setAddWellboreOpen] = useState(false);
  const [addLtdOpen, setAddLtdOpen] = useState(false);
  // Bumped after asset creation modal success so tables refetch
  // without remounting (preserves search/filter/page state).
  const [assetsReloadTick, setAssetsReloadTick] = useState(0);
  const [slotsReloadTick, setSlotsReloadTick] = useState(0);
  // True while a scenario switch re-materializes. The picker and both tables are
  // inert until it settles — the LTD rows are being rebuilt underneath them.
  const [switchingScenario, setSwitchingScenario] = useState(false);
  // A switch that failed, and the scenario it was switching to. Retry re-sends that
  // scenario: if the PATCH failed the draft is unchanged, and if the rebuild failed the BE
  // rebuilds a failed draft even on the same scenario.
  const [scenarioError, setScenarioError] = useState<{
    message: string;
    scenarioId: number;
  } | null>(null);

  // Repointing the draft at another scenario rebuilds its LTD population from that
  // scenario's published inventory, and selections deliberately do NOT carry across
  // (product ruling): every rebuilt row comes back selected. Both tables and the map
  // therefore have to refetch once the job reports ready. `api.setScenario` keeps the
  // form's scenario in step with the draft's, including when the rebuild fails.
  const handleScenarioChange = useCallback(
    async (nextScenarioId: number) => {
      setSwitchingScenario(true);
      setScenarioError(null);
      try {
        await api.setScenario(nextScenarioId);
        if (draftId !== null) fetchMapFeatures(draftId);
        setAssetsReloadTick((t) => t + 1);
        setSlotsReloadTick((t) => t + 1);
      } catch (error) {
        setScenarioError({
          message: error instanceof Error ? error.message : String(error),
          scenarioId: nextScenarioId,
        });
      } finally {
        setSwitchingScenario(false);
      }
    },
    [api, draftId, fetchMapFeatures]
  );

  // Which sellers a new wellbore / LTD asset may be filed under. Derived from
  // the Step 0 selection rather than fetched, so it tracks a source change
  // with no round trip. On the asset list: the matched Tracts' owners.
  const entityOptions = useMemo(
    () =>
      entityOptionsFromSources({
        sourceType,
        sources,
        assetListEntities: assetList.entities,
      }),
    [sourceType, sources, assetList.entities]
  );

  const handleOpenWellbore = useCallback(() => setAddWellboreOpen(true), []);
  const handleOpenLtd = useCallback(() => setAddLtdOpen(true), []);

  const handleAssetAdded = useCallback(() => {
    if (draftId !== null) fetchMapFeatures(draftId);
    setAssetsReloadTick((t) => t + 1);
    setSlotsReloadTick((t) => t + 1);
  }, [draftId, fetchMapFeatures]);

  // Clear stale summary from any previous draft so the Next-button gate
  // starts disabled until the fresh asset query for this draft returns.
  useEffect(() => {
    resetDraftSummary(null);
  }, [resetDraftSummary]);

  // A scenario is mandatory. Next stays shut while a switch is in flight or after one failed,
  // so the user cannot advance on slots that don't match the draft's scenario; Back stays shut
  // mid-switch for the same reason. Back stays shut while Subtract ticks wait to be sent, so the
  // batch goes out with the step still open rather than after it, from the unmount flush; Next
  // stays shut until any asset change is sent and built.
  useEffect(() => {
    setNextEnabled(
      hasSelectedAsset &&
        scenarioId !== null &&
        !switchingScenario &&
        scenarioError === null &&
        !assetChangePending
    );
  }, [
    hasSelectedAsset,
    scenarioId,
    switchingScenario,
    scenarioError,
    assetChangePending,
    setNextEnabled,
  ]);

  useEffect(() => {
    setBackEnabled(!switchingScenario && !ticksPending);
  }, [switchingScenario, ticksPending, setBackEnabled]);

  // Owns the features fetch trigger. The map is a pure consumer — if
  // the trigger lived inside the map, it would re-fire every time the
  // map remounts on a layout change.
  useEffect(() => {
    if (draftId !== null) fetchMapFeatures(draftId);
  }, [draftId, fetchMapFeatures]);

  // Mapbox-gl sizes its canvas on mount from the container's width and
  // doesn't observe parent size changes. A plain `resize` dispatch isn't
  // enough here — the canvas gets stuck. Remounting the component via a
  // layout-derived `key` forces a fresh mapbox instance that reads the
  // current container size at mount time.
  const mapKey = `${showMap ? 'map' : 'no-map'}-${
    showSummary ? 'sum' : 'no-sum'
  }`;

  const layoutClass = classnames(styles.layout, {
    [styles.layoutAll]: showMap && showSummary,
    [styles.layoutNoMap]: !showMap && showSummary,
    [styles.layoutNoSummary]: showMap && !showSummary,
    [styles.layoutNeither]: !showMap && !showSummary,
  });

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <TypeCurveScenarioPicker
          value={scenarioId}
          onChange={handleScenarioChange}
          disabled={switchingScenario || draftId === null || ticksPending}
        />
        {/*
          Switching scenario re-materializes the draft's LTD population on the BE, and
          `api.setScenario` awaits that job, so the wait is as long as a materialization —
          seconds, not milliseconds. Until this, the only feedback was the picker going
          disabled: the tables kept rendering the OLD rows and the step read as frozen.
          Sits beside the picker because that is the control the user just used.
        */}
        {switchingScenario && (
          <InlineLoading
            className={styles.scenarioLoading}
            status="active"
            description="Rebuilding slots…"
          />
        )}
        <Button
          kind="ghost"
          size="sm"
          renderIcon={showMap ? ViewOff : View}
          onClick={() => setShowMap((prev) => !prev)}
        >
          {showMap ? 'Hide Map' : 'Show Map'}
        </Button>
        <Button
          kind="ghost"
          size="sm"
          renderIcon={showSummary ? ViewOff : View}
          onClick={() => setShowSummary((prev) => !prev)}
        >
          {showSummary ? 'Hide Summary' : 'Show Summary'}
        </Button>
      </div>
      {scenarioError !== null && (
        <ActionableNotification
          inline
          kind="error"
          hideCloseButton
          lowContrast
          className={styles.scenarioError}
          title="Rebuilding slots for this scenario failed"
          subtitle={scenarioError.message}
          actionButtonLabel="Retry"
          onActionButtonClick={() =>
            handleScenarioChange(scenarioError.scenarioId)
          }
        />
      )}
      <div className={layoutClass}>
        <div className={styles.tablesCol}>
          <AssetsSlotsSplitPane
            top={
              draftId !== null ? (
                <AssetsTable
                  key={draftId}
                  draftId={draftId}
                  allEntities={sourceType === 'assetList'}
                  reloadTick={assetsReloadTick}
                  onAddWellbore={handleOpenWellbore}
                  onAddLtd={handleOpenLtd}
                  disableSelection={switchingScenario}
                />
              ) : null
            }
            bottom={
              draftId !== null ? (
                // Re-keyed on the scenario so a switch resets filters, sort and
                // paging: the rows underneath are a different population, and
                // carrying a filter over from the previous one would silently
                // hide slots the user has never seen.
                <SlotsTable
                  key={`${draftId}-${scenarioId ?? 'none'}`}
                  draftId={draftId}
                  reloadTick={slotsReloadTick}
                  disableSelection={switchingScenario}
                  disableRemove={switchingScenario}
                />
              ) : null
            }
          />
        </div>
        {showMap && (
          <div
            className={classnames(styles.mapCol, {
              [styles.mapColSolo]: !showSummary,
            })}
          >
            <PackageWizardMap key={mapKey} draftId={draftId} />
          </div>
        )}
        {showSummary && (
          <div className={styles.summaryCol}>
            <Step1SummaryPanel
              sourceType={sourceType}
              sources={sources}
              assetList={
                sourceType === 'assetList'
                  ? {
                      matched: assetList.matched.length,
                      fileName: assetListFileName,
                      entities: assetList.entities,
                    }
                  : undefined
              }
            />
          </div>
        )}
      </div>
      {draftId !== null && (
        <>
          {/*
            "From Existing Package" sources offer the source packages' seller
            entities (captured on each row in select-source-step.tsx) — a
            SUBSET of what the BE `Packages::Creator` inherits at final submit,
            not a mirror of it; see `entityOptionsFromSources`. The list is
            empty when any selected row's sellers are unknown, and each modal
            then explains why it can't file an asset.
          */}
          <AddWellboreModal
            open={addWellboreOpen}
            onClose={() => setAddWellboreOpen(false)}
            entityOptions={entityOptions}
            draftId={draftId}
            onSuccess={handleAssetAdded}
          />
          <AddLtdModal
            open={addLtdOpen}
            onClose={() => setAddLtdOpen(false)}
            entityOptions={entityOptions}
            draftId={draftId}
            onSuccess={handleAssetAdded}
          />
        </>
      )}
    </div>
  );
};
